import React, { useState } from 'react';
import { Clock, X } from 'lucide-react';
import { useLocalTimer } from '../../hooks/useLocalTimer';
import { useDbUser } from '../../hooks/useDbUser';
import { db } from '../../lib/db';
import { toast } from 'sonner';

interface TimerContinueModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function TimerContinueModal({ isOpen, onClose }: TimerContinueModalProps) {
  const { dbUserId } = useDbUser();
  const { currentTaskId, currentTaskTitle, displayTime, stopTimer } = useLocalTimer();
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  if (!isOpen || !currentTaskId) return null;

  const handleContinue = () => {
    // Just close the modal and let timer continue
    onClose();
  };

  const handleStop = async () => {
    if (!notes.trim()) {
      toast.error('Please add notes before stopping the timer');
      return;
    }

    console.log('[TimerContinueModal] Stopping timer with notes:', notes);
    setSaving(true);
    try {
      // Stop timer and get elapsed time
      const elapsedMs = stopTimer();
      console.log('[TimerContinueModal] Timer stopped, elapsed:', elapsedMs, 'ms');
      
      if (elapsedMs > 0 && dbUserId) {
        // Create time entry
        const elapsedMinutes = Math.max(1, Math.floor(elapsedMs / 60000));
        await db.timeEntries.create({
          task_id: currentTaskId,
          user_id: dbUserId,
          start_time: new Date(Date.now() - elapsedMs),
          end_time: new Date(),
          notes: notes,
          break_minutes: 0
        });
        
        const totalMinutes = await db.timeEntries.getTotalMinutesForTask(currentTaskId);
        await db.tasks.update(currentTaskId, dbUserId, {
          time_spent: totalMinutes > 0 ? totalMinutes : undefined,
          time_spent_minutes: totalMinutes > 0 ? totalMinutes : undefined
        });
        
        toast.success(`Time entry saved. ${elapsedMinutes} minute${elapsedMinutes === 1 ? '' : 's'} added.`);
      }
      
      onClose();
    } catch (error) {
      console.error('Error saving time entry:', error);
      toast.error('Failed to save time entry');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center">
            <Clock className="h-6 w-6 text-blue-600 mr-2" />
            <h2 className="text-xl font-semibold">Timer Still Running</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="mb-4">
          <p className="text-gray-600 mb-2">
            You have a timer running from your previous session:
          </p>
          <div className="bg-gray-50 p-3 rounded">
            <p className="font-medium">{currentTaskTitle}</p>
            <p className="text-2xl font-mono text-green-600 mt-1">{displayTime}</p>
          </div>
        </div>
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes (required to stop timer)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What did you work on?"
            className="w-full p-2 border rounded-md"
            rows={3}
          />
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={handleContinue}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Continue Timer
          </button>
          <button
            onClick={handleStop}
            disabled={saving || !notes.trim()}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50"
          >
            Stop & Save
          </button>
        </div>
      </div>
    </div>
  );
}
